import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { createRoot } from 'react-dom/client';

import { getRequestedLanguages } from '../locale/languages';
import { ACCOUNT_TYPES } from '../utils/enum/AccountTypes';
import { AVAILABLE_STATUS } from '../utils/enum/AvailableStatus';
import { addProductToCart, convertNumberToCurrency, sortArrayBySize } from '../utils/functions';
import { CustomerList } from '../utils/models/account';
import { CartItem } from '../utils/models/order';
import {
    ClothingProductData,
    ClothingProductVariant,
    ColorClothingProduct,
    ProductAvailability,
    ProductData,
    SizeClothingProduct,
} from '../utils/models/product';

import { getProductAvailability, getProductChildren } from '../api/products';
import ButtonComponent from '../components/ButtonComponent';
import MessageToastComponent from '../components/MessageToastComponent';
import ClothingQuantitySelectorComponent from '../components/ProductPageComponents/ClothingQuantitySelectorComponent';
import ColorSelectorComponent from '../components/ProductPageComponents/ColorSelectorComponent';
import LoadingSpinnerVariantsComponent from '../components/ProductPageComponents/LoadingSpinnerVariantsComponent';
import MediaGalleryComponent from '../components/ProductPageComponents/MediaGalleryComponent';
import SizeSelectorComponent from '../components/ProductPageComponents/SizeSelectorComponent';
import TableDataComponent from '../components/ProductPageComponents/TableDataComponent';
import TipsCardsComponent from '../components/ProductPageComponents/TipsCardsComponent';
import ModalComponent from '../components/modals/ModalComponent';

declare global {
    interface Window {
        berardi_productData: ProductData;
    }
}

const ClothingProductPage: FC = () => {
    const language = getRequestedLanguages(window.berardi_accountUserData.language);

    const [variants, setVariants] = useState<ClothingProductVariant[]>([]);
    const [colors, setColors] = useState<ColorClothingProduct[]>([]);
    const [sizesPerColor, setSizesPerColor] = useState<SizeClothingProduct[]>([]);
    const [selectedSizePerColor, setSelectedSizePerColor] = useState<SizeClothingProduct | null>(null);
    const [selectedColor, setSelectedColor] = useState<ColorClothingProduct | null>(null);
    const [loadingVariants, setLoadingVariants] = useState<boolean>(false);
    const [loadingSizeAvailability, setLoadingSizeAvailability] = useState<boolean>(false);
    const [selectedQuantity, setSelectedQuantity] = useState<number>(0);
    const [customerCode, setCustomerCode] = useState<string | null>(null);
    const [addedProduct, setAddedProduct] = useState<string>('');

    const currentProduct = (window.berardi_productData as ClothingProductData) ?? null;

    const fetchSizeAvailability = useCallback(async () => {
        if (selectedSizePerColor && customerCode) {
            setLoadingSizeAvailability(true);
            try {
                const sizeQuantity = await getProductAvailability(selectedSizePerColor.sku, customerCode ?? null).then(
                    (result) => {
                        if (result.data.data) {
                            return result.data as ProductAvailability;
                        } else {
                            throw 'error';
                        }
                    },
                );
                const quantityUpdated = sizeQuantity.data.quantity;
                const updatedSizesArray = sizesPerColor.map((size) => {
                    if (size.id == selectedSizePerColor.id) {
                        return {
                            ...size,
                            quantity: {
                                ...quantityUpdated,
                                max: quantityUpdated.max != null ? quantityUpdated.max : 0,
                            },
                        };
                    } else {
                        return size;
                    }
                });
                setSizesPerColor(updatedSizesArray);
                const updatedVariants = variants.map((variant) => {
                    if (variant.sku == selectedSizePerColor.sku) {
                        return {
                            ...variant,
                            quantity: {
                                ...quantityUpdated,
                                max: quantityUpdated.max != null ? quantityUpdated.max : 0,
                            },
                            availableStatus: quantityUpdated.max
                                ? AVAILABLE_STATUS.Available
                                : AVAILABLE_STATUS.NotAvailable,
                        };
                    } else {
                        return variant;
                    }
                });
                setVariants(updatedVariants);
                setSelectedSizePerColor({
                    ...selectedSizePerColor,
                    quantity: {
                        ...quantityUpdated,
                        max: quantityUpdated.max != null ? quantityUpdated.max : 0,
                    },
                });
            } catch (error) {
                console.log('error');
            }
            setLoadingSizeAvailability(false);
        }
    }, [selectedSizePerColor, sizesPerColor, customerCode]);

    const fetchVariants = useCallback(async () => {
        if (currentProduct) {
            setLoadingVariants(true);

            try {
                const productVariants = await getProductChildren(
                    currentProduct.id,
                    [],
                    { per_page: 99999, page: 1 },
                    customerCode,
                )
                    .then((result) => {
                        if (result.data.data) {
                            return result.data.data as ClothingProductVariant[];
                        } else {
                            throw 'error';
                        }
                    })
                    .catch((error) => {
                        throw error;
                    });
                setVariants(productVariants);

                const provColors: ColorClothingProduct[] = [];
                productVariants.forEach((variant) => {
                    const foundInColors = provColors.find((color) => color.id == variant.color.code);
                    if (!foundInColors) {
                        const color = {
                            id: variant.color.code,
                            label: variant.color.value,
                            img: variant.img ? variant.img : currentProduct.defaultImage,
                        };
                        provColors.push(color);
                    }
                });
                setColors(provColors);
                if (provColors.length > 0) {
                    const firstColor = provColors[0];
                    setSelectedColor(firstColor);

                    const filteredSizesPerColor = productVariants.filter(
                        (variant) => variant.color.code == firstColor.id,
                    );
                    handleSetSizes(filteredSizesPerColor);
                }
            } catch (error) {
                console.log(error);
            }

            setLoadingVariants(false);
        }
    }, [customerCode]);

    const handleSetSizes = useCallback((variantsFiltered: ClothingProductVariant[]) => {
        const sizes: SizeClothingProduct[] = variantsFiltered.map((variant) => {
            return {
                id: variant.size.code,
                label: variant.size.value,
                price: variant.price,
                sku: variant.sku,
                quantity: {
                    max: variant.quantity.max,
                    step: variant.quantity.step,
                    min: variant.quantity.min,
                },
            };
        });
        const sortedSizes = sortArrayBySize(sizes);
        setSizesPerColor(sortedSizes);

        const firstSizeAvailable = sizes.find((size) => size.quantity.max && size.quantity.max > 0);
        if (firstSizeAvailable) {
            setSelectedSizePerColor(firstSizeAvailable);
            setSelectedQuantity(firstSizeAvailable.quantity.min);
        }
    }, []);

    const handleSelectColor = useCallback(
        (id: string) => {
            const foundColor = colors.find((color) => color.id == id);
            if (foundColor) {
                setSelectedColor(foundColor);

                const filteredSizesPerColor = variants.filter((variant) => variant.color.code == foundColor.id);
                handleSetSizes(filteredSizesPerColor);
            }
        },
        [colors, variants],
    );

    const handleSelectedSize = useCallback(
        (id: string) => {
            const foundSize = sizesPerColor.find((size) => size.id == id);
            if (foundSize) {
                setSelectedSizePerColor(foundSize);
                setSelectedQuantity(foundSize.quantity.min);
            }
        },
        [sizesPerColor],
    );

    const handleAddToCart = useCallback(() => {
        if (selectedColor && selectedSizePerColor) {
            const foundVariant = variants.find(
                (variant) => variant.size.code == selectedSizePerColor?.id && variant.color.code == selectedColor?.id,
            );
            if (foundVariant) {
                const cartItem: CartItem = {
                    code: '',
                    details: [],
                    id: foundVariant.id,
                    isQuote:
                        window.berardi_accountUserData.accountType == ACCOUNT_TYPES.Prospect ||
                        foundVariant.availableStatus == AVAILABLE_STATUS.NotAvailable
                            ? true
                            : false,
                    featuredImage: foundVariant.img ?? window.berardi_productData.defaultImage,
                    unitPrice: foundVariant.price,
                    quantity: selectedQuantity,
                    quantityDiscount: 0,
                    minQuantityDiscount: 0,
                    sku: foundVariant.berardi_sku.toString(),
                    step: foundVariant.quantity.step,
                    min: foundVariant.quantity.min,
                    max: foundVariant.quantity.max,
                    title: `${window.berardi_productData.title}${selectedColor.label ? ` - ${selectedColor.label}` : ''}${selectedSizePerColor.id ? ` - ${selectedSizePerColor.id}` : ''}`,
                    url: window.berardi_productData.url,
                    availableStatus: foundVariant.availableStatus ?? AVAILABLE_STATUS.Available,
                    colorId: foundVariant.color_code,
                    sizeId: foundVariant.size.code,
                };
                addProductToCart(cartItem, 'EUR');
                setAddedProduct(cartItem.title);
            }
        }
    }, [variants, selectedQuantity, selectedSizePerColor, selectedColor]);

    const currentImage: string = useMemo(() => {
        if (selectedColor) {
            return selectedColor.img;
        }
        return window.berardi_productData.defaultImage ?? '';
    }, [selectedColor]);

    const showAddToCartButton = useMemo(() => {
        if (
            window.berardi_accountUserData.accountType &&
            window.berardi_accountUserData.accountType != ACCOUNT_TYPES.Agent &&
            window.berardi_accountUserData.accountType != ACCOUNT_TYPES.CustomerService &&
            window.berardi_accountUserData.accountType != ACCOUNT_TYPES.Guest
        ) {
            return true;
        }
        return false;
    }, []);

    const disableAddToCart: boolean = useMemo(() => {
        if (!selectedSizePerColor) {
            return true;
        }
        return false;
        // if (
        //     !window.berardi_accountUserData.accountType &&
        //     window.berardi_accountUserData.accountType == ACCOUNT_TYPES.Agent &&
        //     window.berardi_accountUserData.accountType == ACCOUNT_TYPES.CustomerService &&
        //     window.berardi_accountUserData.accountType == ACCOUNT_TYPES.Guest
        // ) {
        //     return true;
        // }
        // if (!selectedSizePerColor) {
        //     return true;
        // }
        // if (selectedSizePerColor.quantity.max && selectedSizePerColor.quantity.max < selectedQuantity) {
        //     return true;
        // }
        // return false;
    }, [selectedSizePerColor]);

    useEffect(() => {
        if (customerCode != null) {
            fetchVariants();
        }
    }, [customerCode]);

    useEffect(() => {
        if (selectedSizePerColor && selectedSizePerColor.quantity.max == null) {
            fetchSizeAvailability();
        }
    }, [selectedSizePerColor]);

    useEffect(() => {
        setSelectedSizePerColor(null);
    }, [selectedColor]);

    useEffect(() => {
        const currentUser: CustomerList | null = localStorage.getItem('br_currentUser')
            ? JSON.parse(localStorage.getItem('br_currentUser') as string)
            : null;
        if (currentUser) {
            setCustomerCode(currentUser.customerAccount);
        } else if (window.berardi_accountUserData && window.berardi_accountUserData.accountCode) {
            setCustomerCode(window.berardi_accountUserData.accountCode ?? '');
        } else {
            setCustomerCode('');
        }
    }, []);

    if (!currentProduct) {
        return null;
    }

    return (
        <div className="product product--small product--left product--thumbnail_slider product--mobile-hide grid grid--1-col grid--2-col-tablet">
            <MediaGalleryComponent image={currentImage} />
            <div className="product__info-wrapper grid__item scroll-trigger animate--slide-in">
                <div className="product__info-container product__column-sticky">
                    <TableDataComponent
                        code={currentProduct.id}
                        tableData={currentProduct.attributes}
                        selectedColor={selectedColor?.label ?? ''}
                    />
                    <div className="br-mainProduct__buttons">
                        {currentProduct.tech_sheet_link && (
                            <a
                                role="link"
                                href={currentProduct.tech_sheet_link}
                                className="br-mainProduct__link"
                                target="_blank"
                            >
                                {language.product.buttons.tech_sheet_download}
                            </a>
                        )}
                        {currentProduct.compliance_sheet_link && (
                            <a
                                role="link"
                                href={currentProduct.compliance_sheet_link}
                                className="br-mainProduct__link"
                                target="_blank"
                            >
                                {language.product.buttons.compliance_sheet_download}
                            </a>
                        )}
                    </div>
                    <div
                        className="br-shortDescription"
                        dangerouslySetInnerHTML={{ __html: currentProduct.description }}
                    />
                    {loadingVariants ? (
                        <LoadingSpinnerVariantsComponent />
                    ) : (
                        <>
                            {selectedColor ? (
                                <ColorSelectorComponent
                                    colors={colors}
                                    selectedColor={selectedColor}
                                    onSelectColor={handleSelectColor}
                                />
                            ) : null}
                            {sizesPerColor.length > 0 ? (
                                <>
                                    <SizeSelectorComponent
                                        sizes={sizesPerColor}
                                        selectedSize={selectedSizePerColor}
                                        onSelectSize={handleSelectedSize}
                                    />
                                    {selectedSizePerColor && customerCode ? (
                                        <>
                                            {window.berardi_accountUserData.accountCode ? (
                                                <div className="br-priceLabel">
                                                    <span className="label">
                                                        {language.product.clothing_selector.unit_price}
                                                    </span>
                                                    <span className="price">
                                                        {convertNumberToCurrency(selectedSizePerColor.price)}
                                                    </span>
                                                </div>
                                            ) : null}
                                            <ClothingQuantitySelectorComponent
                                                quantity={selectedQuantity}
                                                minimum={selectedSizePerColor.quantity.min}
                                                multiple={selectedSizePerColor.quantity.step}
                                                maximum={
                                                    window.berardi_accountUserData
                                                        ? (selectedSizePerColor.quantity.max ?? 0)
                                                        : 9999
                                                }
                                                onChange={setSelectedQuantity}
                                            />
                                            {window.berardi_accountUserData.accountCode ? (
                                                <div className="br-priceLabel">
                                                    <span className="label">
                                                        {language.product.clothing_selector.total_price}
                                                    </span>
                                                    <span className="price">
                                                        {convertNumberToCurrency(
                                                            selectedSizePerColor.price * selectedQuantity,
                                                        )}
                                                    </span>
                                                </div>
                                            ) : null}
                                        </>
                                    ) : null}
                                </>
                            ) : null}
                            {showAddToCartButton && (
                                <div className="br-buttonCartContainer">
                                    <ButtonComponent
                                        text={language.product.clothing_selector.add_to_cart}
                                        style="solid"
                                        onClick={handleAddToCart}
                                        disabled={disableAddToCart}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    <TipsCardsComponent />
                </div>
            </div>
            <MessageToastComponent
                message={addedProduct && `${language.product.table.added_product}${addedProduct}`}
                type="success"
                onHide={() => setAddedProduct('')}
            />
            <ModalComponent showModal={loadingSizeAvailability} isLoading />
        </div>
    );
};

const ClothingProductPageId = document.getElementById('ClothingProductPage');
console.log('🚀 ~ ClothingProductPage:', ClothingProductPageId);
const root = ClothingProductPageId && createRoot(ClothingProductPageId as HTMLElement);
root && root.render(<ClothingProductPage />);
